import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { TID } from "@/constants/testIds";
import { CategoryPicker } from "@/components/add/CategoryPicker";
import { AccountPicker } from "@/components/add/AccountPicker";
import { filterCategoriesByType as fcbt } from "@/lib/categoryFilter";
export function ManualForm({ onDone, prefill }) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [cats, setCats] = useState([]);
  const [ccs, setCcs] = useState([]);
  const [form, setForm] = useState({
    type: "expense", amount: prefill?.amount ?? "", currency: user?.currency || "AED",
    category: "groceries", description: prefill?.description || "", notes: "", date: new Date().toLocaleDateString("en-CA"),
    account_id: "",
  });
  const [blocked, setBlocked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/currencies").then((r) => setCcs(r.data)).catch(() => {});
  }, []);

  const submit = async (e, force = false) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount), force };
      const { data } = await api.post("/transactions", payload);
      toast.success(t("saved"));
      setBlocked(null);
      setForm({ ...form, amount: "", description: "", notes: "" });
      onDone?.();
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && (detail?.budget_blocked || detail?.account_over_balance)) {
        setBlocked(detail);
      } else {
        toast.error(typeof detail === "string" ? detail : t("error"));
      }
    } finally { setBusy(false); }
  };

  return (
    <form data-testid={TID.manualForm} onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label={t("type")}>
        <select data-testid={TID.manualType} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
          {["expense","income","revenue","borrow","lend","repay_borrow","repay_lend","recurring"].map((k) => (
            <option key={k} value={k}>{t(k)}</option>
          ))}
        </select>
      </Field>
      <Field label={t("amount")}>
        <input data-testid={TID.manualAmount} required type="number" step="0.01" min="0" value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
      </Field>
      <Field label={t("currency")}>
        <select data-testid={TID.manualCurrency} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputCls}>
          {ccs.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
        </select>
      </Field>
      <Field label={t("category")} span2>
        <CategoryPicker value={form.category} onChange={(v) => setForm({ ...form, category: v })} cats={fcbt(cats, form.type)} />
      </Field>
      <Field label={t("description")} span2>
        <input data-testid={TID.manualDescription} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
      </Field>
      <Field label={t("date")}>
        <input data-testid={TID.manualDate} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
      </Field>
      <Field label={t("notes")}>
        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
      </Field>
      <Field label={t("accounts")} span2>
        <AccountPicker value={form.account_id} onChange={(v) => setForm({ ...form, account_id: v || "" })} testId="manual-account" />
        {!form.account_id && (<p data-testid="manual-account-error" className="text-[11px] font-semibold text-red-600 mt-1">{lang === "ar" ? "اختر الحساب لتتمكن من الحفظ" : "Select an account to save"}</p>)}
      </Field>
      {blocked && (
        <div className="sm:col-span-2 p-4 rounded-2xl bg-warn/10 border border-warn/40 text-sm space-y-2">
          <p>{blocked.account_over_balance ? t("overdraftWarn").replace("{acc}", blocked.account_name).replace("{bal}", blocked.current_balance).replace("{amt}", blocked.adding) : t("budgetBlocked").replace("{cat}", form.category)}</p>
          <button data-testid={TID.overrideBudget} type="button" onClick={(e) => submit(e, true)} className="px-4 py-2 rounded-2xl bg-warn text-white font-semibold">{t("override")}</button>
        </div>
      )}
      <div className="sm:col-span-2">
        <button data-testid={TID.manualSubmit} type="submit" disabled={busy || !form.account_id}
          className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? "…" : t("save")}
        </button>
      </div>
    </form>
  );
}

const inputCls = "w-full px-4 py-3 rounded-2xl border border-border bg-card focus:border-primary focus:outline-none transition-colors";
function Field({ label, children, span2 }) { return <label className={`space-y-1.5 ${span2 ? "sm:col-span-2" : ""}`}><span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>{children}</label>; }
